// Questionnaire-level settings beyond the basics: endings, quotas, hidden
// fields, loop & merge, languages and translations, and the respondent theme.
import {
  checkAccessibility,
  isFieldName,
  isHexColor,
  languageName,
  operatorLabel,
  sectionRuns,
  SURVEY_LANGUAGES,
  THEME_FONTS,
  THEME_ZOOMS,
  type Condition,
  type Definition,
  type Ending,
  type Question,
  type QuestionTranslation,
  type Quota,
  type Theme,
  type Translation,
} from '@survey/core';
import { Accessibility, ChevronRight, Flag, Languages, Link2, Palette, Plus, Repeat, Target, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { CollapsibleCard } from '../CollapsibleCard';
import { useI18n } from '../../i18n';
import { Field, Modal, Switch } from '../ui';
import { ConditionRow, conditionSources, defaultValue, operatorsFor } from './LogicEditor';
import { OptionImage } from './MediaEditor';
import { newId } from './model';

type Update = (fn: (d: Definition) => Definition) => void;

export function EndingsCard({ def, update }: { def: Definition; update: Update }) {
  const { t } = useI18n();
  const endings = def.endings ?? [];
  const set = (next: Ending[]) => update((d) => ({ ...d, endings: next.length ? next : undefined }));
  const patch = (id: string, p: Partial<Ending>) => set(endings.map((e) => (e.id === id ? { ...e, ...p } : e)));
  const remove = (id: string) =>
    update((d) => {
      const rest = (d.endings ?? []).filter((e) => e.id !== id);
      return {
        ...d,
        endings: rest.length ? rest : undefined,
        // Jumps to a removed ending would point nowhere.
        questions: d.questions.map((q) => (q.jumpIf?.targetEndingId === id ? { ...q, jumpIf: undefined } : q)),
      };
    });
  const used = (id: string) => def.questions.filter((q) => q.jumpIf?.targetEndingId === id).length;
  return (
    <CollapsibleCard
      storageKey="survey.settings.open.endings"
      defaultOpen={false}
      hideClosedActions
      sub={endings.length ? t('settings.endings_count', { count: endings.length }) : t('settings.endings_none_short')}
      icon={<Flag />}
      title={t('settings.endings')}
      actions={
        <button
          className="btn sm"
          onClick={() =>
            set([
              ...endings,
              {
                id: newId('end'),
                name: t('settings.ending_n', { n: endings.length + 1 }),
                message: '',
              },
            ])
          }
        >
          <Plus /> {t('common.add')}
        </button>
      }
    >
      <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
        {endings.length === 0 && <p className="small muted">{t('settings.no_endings')}</p>}
        {endings.map((e) => (
          <div key={e.id} className="setting-item">
            <div className="row gap-8">
              <input className="input" value={e.name} aria-label={t('settings.ending_name')} placeholder={t('settings.ending_name')} onChange={(x) => patch(e.id, { name: x.target.value })} />
              <span className="pill">{t('settings.used_by', { count: used(e.id) })}</span>
              <button className="btn ghost sm icon" aria-label={t('settings.remove_ending')} onClick={() => remove(e.id)}>
                <Trash2 />
              </button>
            </div>
            <textarea className="textarea" style={{ minHeight: 60 }} value={e.message} maxLength={2000} placeholder={t('settings.ending_message')} aria-label={t('settings.ending_message')} onChange={(x) => patch(e.id, { message: x.target.value })} />
            <input className="input" value={e.redirectUrl ?? ''} placeholder={t('settings.redirect_placeholder')} aria-label={t('settings.redirect')} onChange={(x) => patch(e.id, { redirectUrl: x.target.value.trim() || undefined })} />
          </div>
        ))}
        {endings.length > 0 && <p className="hint">{t('settings.endings_hint')}</p>}
      </div>
    </CollapsibleCard>
  );
}

export function QuotasCard({ def, update, surveyId }: { def: Definition; update: Update; surveyId: string }) {
  const { t, locale } = useI18n();
  const quotas = def.quotas ?? [];
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [editing, setEditing] = useState<string | null>(null);
  useEffect(() => {
    api.surveyQuotas(surveyId).then((r) => setCounts(r.counts), () => {});
  }, [surveyId]);
  const sources = conditionSources(def, def.questions);
  const set = (next: Quota[]) => update((d) => ({ ...d, quotas: next.length ? next : undefined }));
  const patch = (id: string, p: Partial<Quota>) => set(quotas.map((q) => (q.id === id ? { ...q, ...p } : q)));
  const firstCondition = (): Condition[] => {
    const src = sources.find((q) => q.type === 'single_choice' || q.type === 'dropdown') ?? sources[0];
    return src ? [{ questionId: src.id, operator: operatorsFor(src)[0]!, value: defaultValue(src) }] : [];
  };
  // Closed quota rows say in words which answers they count, e.g. "Gender = Female".
  const quotaRule = (q: Quota) => {
    if (!q.conditions.length) return t('settings.quota_no_conditions');
    const label = (src: Question | undefined, v: unknown) => {
      const list = Array.isArray(v) ? v : [v];
      return list.map((x) => src?.options?.find((o) => o.id === x)?.label ?? (typeof x === 'boolean' ? t(x ? 'common.yes' : 'common.no') : String(x ?? ''))).join(', ');
    };
    return q.conditions
      .map((c) => {
        const src = def.questions.find((x) => x.id === c.questionId);
        return `${src?.title || '?'} ${operatorLabel(c.operator, locale)} ${label(src, c.value)}`.trim();
      })
      .join(q.match === 'all' ? ' · ' : ` ${t('settings.quota_or')} `);
  };
  return (
    <CollapsibleCard
      storageKey="survey.settings.open.quotas"
      defaultOpen={false}
      hideClosedActions
      sub={quotas.length ? quotas.map((q) => `${q.name} ${counts[q.id] ?? 0}/${q.limit}`).join(' · ') : t('settings.quotas_none_short')}
      icon={<Target />}
      title={t('settings.quotas')}
      actions={
        <button
          className="btn sm"
          disabled={!sources.length}
          onClick={() => {
            const id = newId('quota');
            set([
              ...quotas,
              {
                id,
                name: t('settings.quota_n', { n: quotas.length + 1 }),
                match: 'all',
                conditions: firstCondition(),
                limit: 100,
              },
            ]);
            setEditing(id);
          }}
        >
          <Plus /> {t('common.add')}
        </button>
      }
    >
      <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
        {quotas.length === 0 && <p className="small muted">{t('settings.no_quotas')}</p>}
        {quotas.map((q) => {
          const n = counts[q.id] ?? 0;
          const open = editing === q.id;
          return (
            <div key={q.id} className={`setting-item quota-item ${open ? 'open' : ''}`}>
              <div className="quota-row">
                <button type="button" className="quota-name" aria-expanded={open} onClick={() => setEditing(open ? null : q.id)}>
                  <ChevronRight className={`chev ${open ? 'open' : ''}`} />
                  <span className="ellipsis">{q.name || t('settings.quota_name')}</span>
                </button>
                <div className="quota-meter" aria-hidden>
                  <span
                    style={{
                      width: `${Math.min(100, (n / Math.max(1, q.limit)) * 100)}%`,
                    }}
                  />
                </div>
                <span className={`pill ${n >= q.limit ? 'orange' : ''}`} title={t('settings.quota_count_title')}>
                  {t('settings.quota_count', { n, limit: q.limit })}
                </span>
                <button className="btn ghost sm icon" aria-label={t('settings.remove_quota')} onClick={() => set(quotas.filter((x) => x.id !== q.id))}>
                  <Trash2 />
                </button>
              </div>
              {!open && <div className="small muted quota-rule">{quotaRule(q)}</div>}
              {open && (
                <>
                  <div className="row gap-8 wrap">
                    <input className="input grow" style={{ minWidth: 160 }} value={q.name} aria-label={t('settings.quota_name')} onChange={(e) => patch(q.id, { name: e.target.value })} />
                    <label className="row gap-8 small">
                      {t('settings.quota_limit')}
                      <input
                        className="input"
                        type="number"
                        min={1}
                        style={{ width: 96 }}
                        value={q.limit}
                        onChange={(e) =>
                          patch(q.id, {
                            limit: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                          })
                        }
                      />
                    </label>
                  </div>
                  <div className="row gap-8 wrap small">
                    {t('settings.quota_counts_when')}
                    <select
                      className="select"
                      style={{ width: 'auto', height: 30 }}
                      value={q.match}
                      onChange={(e) => patch(q.id, { match: e.target.value as 'all' | 'any' })}
                      aria-label={t('logic.match')}
                    >
                      <option value="all">{t('logic.all')}</option>
                      <option value="any">{t('logic.any')}</option>
                    </select>
                    {t('logic.of_these')}
                  </div>
                  {q.conditions.map((c, i) => (
                    <ConditionRow
                      key={i}
                      def={def}
                      sources={sources}
                      condition={c}
                      onChange={(next) =>
                        patch(q.id, {
                          conditions: q.conditions.map((x, j) => (j === i ? next : x)),
                        })
                      }
                      onRemove={() =>
                        patch(q.id, {
                          conditions: q.conditions.filter((_, j) => j !== i),
                        })
                      }
                    />
                  ))}
                  <div>
                    <button
                      className="btn sm ghost"
                      onClick={() =>
                        patch(q.id, {
                          conditions: [...q.conditions, ...firstCondition()],
                        })
                      }
                    >
                      <Plus /> {t('logic.add')}
                    </button>
                  </div>
                  <input
                    className="input"
                    value={q.message ?? ''}
                    maxLength={2000}
                    placeholder={t('settings.quota_message')}
                    aria-label={t('settings.quota_message')}
                    onChange={(e) => patch(q.id, { message: e.target.value || undefined })}
                  />
                </>
              )}
            </div>
          );
        })}
        {quotas.length > 0 && <p className="hint">{t('settings.quotas_hint')}</p>}
      </div>
    </CollapsibleCard>
  );
}

export function HiddenFieldsCard({ def, update, code }: { def: Definition; update: Update; code: string }) {
  const { t } = useI18n();
  const fields = def.settings.hiddenFields ?? [];
  const [name, setName] = useState('');
  const valid = isFieldName(name) && !fields.some((f) => f.toLowerCase() === name.toLowerCase()) && name !== 'loop' && name !== 'score';
  const set = (next: string[]) => update((d) => ({ ...d, settings: { ...d.settings, hiddenFields: next.length ? next : undefined } }));
  const example = `${window.location.origin}/s/${code}?${(fields.length ? fields : ['source']).map((f) => `${f}=…`).join('&')}`;
  return (
    <CollapsibleCard
      storageKey="survey.settings.open.hidden"
      defaultOpen={false}
      sub={fields.length ? fields.join(', ') : t('settings.none_short')}
      icon={<Link2 />}
      title={t('settings.hidden_fields')}
    >
      <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
        {fields.length > 0 && (
          <div className="row gap-8 wrap">
            {fields.map((f) => (
              <span key={f} className="chip">
                <code>{f}</code>
                <button className="btn ghost sm icon" aria-label={t('settings.remove_field', { name: f })} onClick={() => set(fields.filter((x) => x !== f))}>
                  <X />
                </button>
              </span>
            ))}
          </div>
        )}
        <form
          className="row gap-8"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) {
              set([...fields, name]);
              setName('');
            }
          }}
        >
          <input className="input" value={name} maxLength={32} placeholder={t('settings.field_placeholder')} aria-label={t('settings.field_name')} onChange={(e) => setName(e.target.value.replace(/\s/g, '_'))} />
          <button className="btn sm" disabled={!valid}>
            <Plus /> {t('common.add')}
          </button>
        </form>
        <p className="hint">{t('settings.hidden_fields_hint')}</p>
        <code className="code-sample">{example}</code>
      </div>
    </CollapsibleCard>
  );
}

export function LoopsCard({ def, update }: { def: Definition; update: Update }) {
  const { t } = useI18n();
  const runs = sectionRuns(def).filter((r) => r.title);
  const titles = [...new Set(runs.map((r) => r.title))];
  const setLoop = (title: string, loopOver: string | undefined) =>
    update((d) => {
      const opts = { ...d.settings.sectionOptions };
      opts[title] = { ...opts[title], loopOver };
      if (!loopOver) delete opts[title]!.loopOver;
      if (!Object.keys(opts[title]!).length) delete opts[title];
      return { ...d, settings: { ...d.settings, sectionOptions: Object.keys(opts).length ? opts : undefined } };
    });
  const looped = titles.filter((title) => def.settings.sectionOptions?.[title]?.loopOver).length;
  return (
    <CollapsibleCard
      storageKey="survey.settings.open.loops"
      defaultOpen={false}
      sub={looped ? t('settings.loops_count', { count: looped }) : t('settings.none_short')}
      icon={<Repeat />}
      title={t('settings.loops')}
    >
      <div className="card-pad stack gap-12" style={{ paddingTop: 0 }}>
        {titles.length === 0 && <p className="small muted">{t('settings.loops_need_sections')}</p>}
        {titles.map((title) => {
          const first = def.questions.findIndex((q) => q.sectionTitle?.trim() === title);
          const candidates = def.questions.slice(0, first).filter((q) => q.type === 'multiple_choice' && q.sectionTitle?.trim() !== title);
          const current = def.settings.sectionOptions?.[title]?.loopOver;
          return (
            <Field key={title} label={title} htmlFor={`loop-${title}`}>
              <select id={`loop-${title}`} className="select" value={current ?? ''} onChange={(e) => setLoop(title, e.target.value || undefined)} disabled={!candidates.length && !current}>
                <option value="">{candidates.length ? t('settings.loop_none') : t('settings.loop_no_source')}</option>
                {candidates.map((q) => (
                  <option key={q.id} value={q.id}>
                    {t('settings.loop_for_each', { n: def.questions.indexOf(q) + 1, title: q.title || t('common.untitled_question') })}
                  </option>
                ))}
              </select>
            </Field>
          );
        })}
        <p className="hint">{t('settings.loops_hint')}</p>
      </div>
    </CollapsibleCard>
  );
}

export function ThemeCard({ def, update }: { def: Definition; update: Update }) {
  const { t } = useI18n();
  const theme = def.theme ?? {};
  const set = (p: Partial<Theme>) =>
    update((d) => {
      const next = { ...d.theme, ...p };
      for (const k of Object.keys(next) as (keyof Theme)[]) if (next[k] === undefined || next[k] === '') delete next[k];
      return { ...d, theme: Object.keys(next).length ? next : undefined };
    });
  const color = (key: 'primaryColor' | 'backgroundColor', label: string, fallback: string) => (
    <Field label={label}>
      <div className="row gap-8">
        <input type="color" className="color-input" value={isHexColor(theme[key]) ? theme[key] : fallback} aria-label={label} onChange={(e) => set({ [key]: e.target.value })} />
        <input className="input" value={theme[key] ?? ''} placeholder={t('settings.theme_default')} maxLength={7} onChange={(e) => set({ [key]: e.target.value.trim() || undefined })} />
        {theme[key] && (
          <button className="btn ghost sm icon" aria-label={t('settings.theme_reset')} onClick={() => set({ [key]: undefined })}>
            <X />
          </button>
        )}
      </div>
    </Field>
  );
  return (
    <CollapsibleCard
      storageKey="survey.settings.open.theme"
      defaultOpen={false}
      sub={Object.keys(theme).length ? t('settings.theme_custom') : t('settings.theme_default_short')}
      icon={<Palette />}
      title={t('settings.theme')}
    >
      <div className="card-pad form-grid" style={{ paddingTop: 0 }}>
        <div className="grid cols-2">
          {color('primaryColor', t('settings.theme_primary'), '#3fae4c')}
          {color('backgroundColor', t('settings.theme_background'), '#edece8')}
        </div>
        <div className="grid cols-2">
          <Field label={t('settings.theme_font')}>
            <select className="select" value={theme.font ?? 'system'} onChange={(e) => set({ font: e.target.value === 'system' ? undefined : (e.target.value as Theme['font']) })}>
              {THEME_FONTS.map((f) => (
                <option key={f} value={f}>
                  {t(`settings.font_${f}`)}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('settings.theme_zoom')} hint={t('settings.theme_zoom_hint')} htmlFor="theme-zoom">
            <select id="theme-zoom" className="select" value={theme.zoom ?? 1} onChange={(e) => set({ zoom: Number(e.target.value) === 1 ? undefined : Number(e.target.value) })}>
              {THEME_ZOOMS.map((z) => (
                <option key={z} value={z}>
                  {z === 1 ? t('settings.zoom_standard') : `${Math.round(z * 100)}%`}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('settings.theme_logo')}>
            <div className="row gap-8">
              <OptionImage url={theme.logoUrl} label={t('settings.theme_logo')} onChange={(logoUrl) => set({ logoUrl })} />
              <span className="small muted">{theme.logoUrl ? t('settings.logo_set') : t('settings.logo_hint')}</span>
            </div>
          </Field>
        </div>
        <div className="theme-preview" style={{ background: isHexColor(theme.backgroundColor) ? theme.backgroundColor : undefined }}>
          <div className="theme-preview-card" style={theme.zoom && THEME_ZOOMS.includes(theme.zoom) ? { zoom: theme.zoom } : undefined}>
            <span className="theme-dot" style={{ background: isHexColor(theme.primaryColor) ? theme.primaryColor : undefined }} />
            <span className="small">{t('settings.theme_preview')}</span>
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
}

// ---------- Languages ----------

interface Entry {
  id: string;
  group: string;
  label: string;
  source: string;
  get: (tr: Translation) => string | undefined;
  set: (tr: Translation, v: string | undefined) => Translation;
  long?: boolean;
}

function setQ(tr: Translation, qid: string, fn: (qt: QuestionTranslation) => QuestionTranslation): Translation {
  const questions = { ...tr.questions };
  const next = fn({ ...questions[qid] });
  for (const k of Object.keys(next) as (keyof QuestionTranslation)[]) {
    const v = next[k];
    if (v === undefined || v === '' || (typeof v === 'object' && !Object.keys(v).length)) delete next[k];
  }
  if (Object.keys(next).length) questions[qid] = next;
  else delete questions[qid];
  return { ...tr, questions: Object.keys(questions).length ? questions : undefined };
}

function setMap(map: Record<string, string> | undefined, key: string, v: string | undefined) {
  const out = { ...map };
  if (v) out[key] = v;
  else delete out[key];
  return Object.keys(out).length ? out : undefined;
}

/** Everything translatable in the questionnaire, with getters/setters into a Translation. */
function translatable(def: Definition, survey: { title: string; description: string }, t: ReturnType<typeof useI18n>['t']): Entry[] {
  const out: Entry[] = [];
  const top = (key: 'title' | 'description' | 'welcomeMessage' | 'thankYouMessage', label: string, source: string, long = false) =>
    source && out.push({ id: key, group: t('tr.survey'), label, source, long, get: (tr) => tr[key], set: (tr, v) => ({ ...tr, [key]: v }) });
  top('title', t('tr.title'), survey.title);
  top('description', t('tr.description'), survey.description, true);
  top('welcomeMessage', t('builder.welcome_message'), def.welcomeMessage ?? '', true);
  top('thankYouMessage', t('builder.thankyou_message'), def.thankYouMessage ?? '', true);
  for (const title of new Set(def.questions.map((q) => q.sectionTitle?.trim()).filter((x): x is string => !!x))) {
    out.push({ id: `s:${title}`, group: t('tr.sections'), label: t('tr.section'), source: title, get: (tr) => tr.sections?.[title], set: (tr, v) => ({ ...tr, sections: setMap(tr.sections, title, v) }) });
  }
  def.questions.forEach((q: Question, i) => {
    const group = `${i + 1}. ${q.title || t('common.untitled_question')}`;
    const field = (key: 'title' | 'description' | 'tooltip' | 'placeholder' | 'minLabel' | 'maxLabel', label: string, source: string | undefined, long = false) =>
      source && out.push({ id: `${q.id}:${key}`, group, label, source, long, get: (tr) => tr.questions?.[q.id]?.[key], set: (tr, v) => setQ(tr, q.id, (qt) => ({ ...qt, [key]: v })) });
    field('title', t('tr.question'), q.title);
    field('description', t('qe.help_text'), q.description);
    field('tooltip', t('qe.tooltip'), q.tooltip, true);
    field('placeholder', t('qe.placeholder'), q.placeholder);
    field('minLabel', t('qe.low_label'), q.scale?.minLabel);
    field('maxLabel', t('qe.high_label'), q.scale?.maxLabel);
    for (const [kind, list] of [['options', q.carryForward ? [] : (q.options ?? [])], ['rows', q.rows ?? []]] as const) {
      list.forEach((o) =>
        out.push({
          id: `${q.id}:${kind}:${o.id}`,
          group,
          label: kind === 'rows' ? t('qe.rows') : t('qe.options'),
          source: o.label,
          get: (tr) => tr.questions?.[q.id]?.[kind]?.[o.id],
          set: (tr, v) => setQ(tr, q.id, (qt) => ({ ...qt, [kind]: setMap(qt[kind], o.id, v) })),
        }),
      );
    }
  });
  for (const e of def.endings ?? [])
    if (e.message) out.push({ id: `e:${e.id}`, group: t('settings.endings'), label: e.name, source: e.message, long: true, get: (tr) => tr.endings?.[e.id], set: (tr, v) => ({ ...tr, endings: setMap(tr.endings, e.id, v) }) });
  for (const q of def.quotas ?? [])
    if (q.message) out.push({ id: `q:${q.id}`, group: t('settings.quotas'), label: q.name, source: q.message, long: true, get: (tr) => tr.quotas?.[q.id], set: (tr, v) => ({ ...tr, quotas: setMap(tr.quotas, q.id, v) }) });
  return out;
}

export function LanguagesCard({ def, update, survey }: { def: Definition; update: Update; survey: { title: string; description: string } }) {
  const { t } = useI18n();
  const base = def.language || 'en';
  const extra = Object.keys(def.translations ?? {});
  const [editing, setEditing] = useState<string | null>(null);
  const entries = useMemo(() => translatable(def, survey, t), [def, survey, t]);
  const progress = (lang: string) => {
    const tr = def.translations?.[lang] ?? {};
    return entries.filter((e) => e.get(tr)).length;
  };
  const available = SURVEY_LANGUAGES.filter((l) => l.code !== base && !extra.includes(l.code));
  return (
    <CollapsibleCard
      storageKey="survey.settings.open.languages"
      defaultOpen={false}
      sub={[languageName(base), ...extra.map((l) => `${languageName(l)} ${Math.round((progress(l) / Math.max(1, entries.length)) * 100)}%`)].join(' · ')}
      icon={<Languages />}
      title={t('settings.languages')}
    >
      <div className="card-pad form-grid" style={{ paddingTop: 0 }}>
        <Field label={t('settings.base_language')} hint={t('settings.base_language_hint')}>
          <select
            className="select"
            value={base}
            onChange={(e) => {
              const lang = e.target.value;
              update((d) => {
                const translations = { ...d.translations };
                delete translations[lang];
                return { ...d, language: lang === 'en' ? undefined : lang, translations: Object.keys(translations).length ? translations : undefined };
              });
            }}
          >
            {SURVEY_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </Field>
        {extra.map((lang) => (
          <div key={lang} className="row gap-8 setting-item" style={{ flexDirection: 'row', alignItems: 'center' }}>
            <strong className="grow">{languageName(lang)}</strong>
            <span className="small muted">{t('settings.translated', { n: progress(lang), total: entries.length })}</span>
            <button className="btn sm" onClick={() => setEditing(lang)}>
              {t('settings.edit_translation')}
            </button>
            <button
              className="btn ghost sm icon"
              aria-label={t('settings.remove_language', { name: languageName(lang) })}
              onClick={() =>
                update((d) => {
                  const translations = { ...d.translations };
                  delete translations[lang];
                  return { ...d, translations: Object.keys(translations).length ? translations : undefined };
                })
              }
            >
              <Trash2 />
            </button>
          </div>
        ))}
        {available.length > 0 && (
          <Field label={t('settings.add_language')}>
            <select
              className="select"
              value=""
              onChange={(e) => {
                const lang = e.target.value;
                if (!lang) return;
                update((d) => ({ ...d, translations: { ...d.translations, [lang]: {} } }));
                setEditing(lang);
              }}
            >
              <option value="">{t('settings.choose_language')}</option>
              {available.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </Field>
        )}
        <p className="hint">{t('settings.languages_hint')}</p>
      </div>
      {editing && <TranslationModal lang={editing} def={def} entries={entries} update={update} onClose={() => setEditing(null)} />}
    </CollapsibleCard>
  );
}

function TranslationModal({ lang, def, entries, update, onClose }: { lang: string; def: Definition; entries: Entry[]; update: Update; onClose: () => void }) {
  const { t } = useI18n();
  const [onlyMissing, setOnlyMissing] = useState(false);
  const tr = def.translations?.[lang] ?? {};
  const shown = entries.filter((e) => !onlyMissing || !e.get(tr));
  const groups = [...new Set(shown.map((e) => e.group))];
  const set = (e: Entry, v: string) =>
    update((d) => ({ ...d, translations: { ...d.translations, [lang]: e.set(d.translations?.[lang] ?? {}, v.trim() ? v : undefined) } }));
  const done = entries.filter((e) => e.get(tr)).length;
  return (
    <Modal title={t('tr.modal_title', { name: languageName(lang) })} onClose={onClose} wide>
      <div className="row gap-12 wrap mb-16">
        <span className="small muted grow">{t('settings.translated', { n: done, total: entries.length })}</span>
        <Switch checked={onlyMissing} onChange={setOnlyMissing} label={t('tr.only_missing')} />
      </div>
      <div className="stack gap-16 translation-list" dir={['ar', 'he', 'fa', 'ur'].includes(lang) ? 'rtl' : undefined}>
        {groups.map((g) => (
          <section key={g} className="stack gap-8">
            <h3 className="small strong">{g}</h3>
            {shown
              .filter((e) => e.group === g)
              .map((e) => (
                <label key={e.id} className="translation-row">
                  <span className="small muted">
                    {e.label}: <span className="translation-source">{e.source}</span>
                  </span>
                  {e.long ? (
                    <textarea className="textarea" style={{ minHeight: 56 }} value={e.get(tr) ?? ''} placeholder={e.source} onChange={(x) => set(e, x.target.value)} />
                  ) : (
                    <input className="input" value={e.get(tr) ?? ''} placeholder={e.source} onChange={(x) => set(e, x.target.value)} />
                  )}
                </label>
              ))}
          </section>
        ))}
        {!shown.length && <p className="small muted">{t('tr.all_done')}</p>}
      </div>
    </Modal>
  );
}

/** WCAG-based advice for the questionnaire; each item links to its question. */
export function AccessibilityCard({ def, onOpenQuestion }: { def: Definition; onOpenQuestion: (id: string) => void }) {
  const { t, locale } = useI18n();
  const issues = useMemo(() => checkAccessibility(def, locale), [def, locale]);
  const errors = issues.filter((i) => i.level === 'error').length;
  return (
    <CollapsibleCard
      storageKey="survey.settings.open.a11y"
      defaultOpen={errors > 0}
      sub={issues.length ? t('a11y.summary', { errors, warnings: issues.length - errors }) : t('a11y.all_good')}
      badge={errors > 0 ? <span className="pill red">{errors}</span> : null}
      icon={<Accessibility />}
      title={t('a11y.title')}
    >
      <div className="card-pad stack gap-8" style={{ paddingTop: 0 }}>
        {issues.map((i, n) => (
          <div key={n} className={`a11y-item ${i.level}`}>
            <span className={`pill ${i.level === 'error' ? 'red' : 'yellow'}`}>{t(i.level === 'error' ? 'a11y.fix' : 'a11y.consider')}</span>
            <span className="grow small">{i.message}</span>
            <span className="small muted" title={t('a11y.wcag_title')}>
              WCAG {i.wcag}
            </span>
            {i.questionId && (
              <button className="btn ghost sm" onClick={() => onOpenQuestion(i.questionId!)}>
                {t('a11y.open')}
              </button>
            )}
          </div>
        ))}
        <p className="hint">{t('a11y.hint')}</p>
      </div>
    </CollapsibleCard>
  );
}
